// @rpg-unit/instrument-presets: instruments for @rpg/audio's music (tier 3; composition framework, decision 0077).
// Ten recipes cut from spike 002's music pass (2026-10-19): a breathy flute, an FM electric piano, a harp and a nylon
// guitar (bodied plucks), a warm pad, a round bass, an FM bell, and a taiko-like drum, a shaker and a tom for a fight's
// layer. Each is a multisample: rendered at several roots (fewer on a phone) and replayed from the nearest, with a
// sustain loop where it holds, a release, a level, a stereo spread by pitch and sends into the music's room and echo.
//
// A game takes them three ways:
//   - as content: list this package in its content folder's `include.json` (@rpg/content reads `src/content`) or add
//     `INSTRUMENT_PRESET_CONTENT` to the tables it loads in the browser; the rows are the `instruments` table;
//   - as @rpg/audio instruments: `toInstrument(row, knobs)` or `instrumentsFor(rows, ids, knobs)` for a director's
//     `music.instruments`;
//   - tuned by perceptual knobs: `room` (drier or wetter) and `light` (lighter to load: the phone's roots).
// Moods and ensembles name these instruments by id, as data; the feature that plays them resolves the ids.
import { checkRecipe, midiHz, peakOf, renderSound, type Instrument, type SoundRecipe } from "@rpg/audio";
import table from "./content/tables/instruments.json";
import schema from "./content/schemas/instruments.schema.json";

/** This unit's version (the manifest's and package.json's). */
export const INSTRUMENT_PRESETS_VERSION = "0.1.0" as const;

/** The kinds of instrument (what a composer swaps like for like). */
export type InstrumentFamily = "wind" | "keys" | "plucked" | "pad" | "bass" | "bell" | "drum";

/** One instrument, as its schema describes it (`src/content/schemas/instruments.schema.json`). */
export interface InstrumentPreset {
  id: string;
  family: InstrumentFamily;
  /** Plays a tune at the note asked (false: percussion at fixed notes). */
  pitched: boolean;
  /** The MIDI note the recipe sounds at. */
  ref: number;
  recipe: SoundRecipe;
  /** The desktop's multisample roots (MIDI; default `[ref]`). */
  roots?: number[];
  /** The phone's roots: fewer, so less to render at load (default `roots`). */
  lowRoots?: number[];
  /** A sustained instrument loops this stretch (seconds into the render). */
  sustain?: number[];
  release?: number;
  gain?: number;
  spread?: number;
  reverb?: number;
  echo?: number;
  /** Why it is made this way. */
  reason: string;
}

/** The instruments as a content table (`{ table: "instruments", version, rows }`). */
export const INSTRUMENT_PRESETS_TABLE = table as unknown as { table: "instruments"; version: number; rows: InstrumentPreset[] };

/** The table's row schema (JSON Schema). */
export const INSTRUMENT_PRESETS_SCHEMA = schema as Record<string, unknown>;

/** The content this unit adds to a game, in @rpg/content's shape: its tables and their schemas by table name. */
export const INSTRUMENT_PRESET_CONTENT = {
  package: "@rpg-unit/instrument-presets",
  version: INSTRUMENT_PRESETS_VERSION,
  tables: [INSTRUMENT_PRESETS_TABLE],
  schemas: { instruments: INSTRUMENT_PRESETS_SCHEMA },
} as const;

/** The perceptual knobs (rule 13): what a person would say, and what each changes. */
export const INSTRUMENT_PRESET_KNOBS = {
  room: "drier or wetter: scales every instrument's sends into the music's room and echo (1 as tuned, 0 dry, 1.5 half as wet again; capped at 1).",
  light: "lighter to load: the phone's roots (fewer samples rendered at load, notes shifted further from a root, up to 6 semitones); off on a desktop.",
} as const;

/** Knob values. */
export interface InstrumentKnobs {
  /** Scales the reverb and echo sends (default 1, as tuned). */
  room?: number;
  /** Use the phone's roots (`lowRoots`; default false). */
  light?: boolean;
}

const round = (x: number): number => Math.round(x * 1e6) / 1e6;

/** A preset as an @rpg/audio `Instrument` (only the fields the engine reads), turned by the knobs. With no knobs it is
 *  exactly the row's values. */
export function toInstrument(p: InstrumentPreset, knobs: InstrumentKnobs = {}): Instrument {
  const room = knobs.room ?? 1;
  if (!(room >= 0)) throw new Error(`instrument presets: room must be ≥ 0 (got ${room})`);
  const roots = knobs.light ? (p.lowRoots ?? p.roots) : p.roots;
  const send = (x: number): number => (room === 1 ? x : round(Math.min(1, x * room)));
  return {
    ref: p.ref,
    recipe: p.recipe,
    ...(roots ? { roots } : {}),
    ...(p.sustain ? { sustain: [p.sustain[0]!, p.sustain[1]!] as [number, number] } : {}),
    ...(p.release !== undefined ? { release: p.release } : {}),
    ...(p.gain !== undefined ? { gain: p.gain } : {}),
    ...(p.spread !== undefined ? { spread: p.spread } : {}),
    ...(p.reverb !== undefined ? { reverb: send(p.reverb) } : {}),
    ...(p.echo !== undefined ? { echo: send(p.echo) } : {}),
  };
}

/** The named instruments (every row when `ids` is omitted) as a director's `music.instruments`, in the rows' order.
 *  An id no row has throws: the feature that resolves ids reports it before this. */
export function instrumentsFor(rows: readonly InstrumentPreset[] = INSTRUMENT_PRESETS_TABLE.rows, ids?: Iterable<string>, knobs: InstrumentKnobs = {}): Record<string, Instrument> {
  const want = ids ? new Set(ids) : null;
  if (want) for (const id of want) if (!rows.some((r) => r.id === id)) throw new Error(`instrument presets: no instrument "${id}"`);
  const out: Record<string, Instrument> = {};
  for (const r of rows) if (!want || want.has(r.id)) out[r.id] = toInstrument(r, knobs);
  return out;
}

/** The largest shift (semitones) a note between the lowest and highest root makes to its nearest root: half the widest
 *  gap between neighbouring roots. A single root shifts nothing inside its "range" (the root itself). */
export function widestShift(roots: readonly number[]): number {
  const r = [...roots].sort((a, b) => a - b);
  let gap = 0;
  for (let i = 1; i < r.length; i++) gap = Math.max(gap, r[i]! - r[i - 1]!);
  return gap / 2;
}

/** The bounds the checks hold a preset to: the widest shift between the desktop's roots and between the phone's
 *  (semitones), and how far a pitched instrument's recipe may sound from its `ref` (cents). */
export const INSTRUMENT_BOUNDS = { desktopShift: 3, phoneShift: 6, cents: 15 } as const;

/** Autocorrelation pitch (Hz) of a stretch of samples, to a fraction of a sample (a parabola through the peak), between
 *  60 Hz and 1.2 kHz. */
export function pitchOf(x: Float32Array, sr: number): number {
  const ac = (k: number): number => {
    let s = 0;
    for (let i = 0; i + k < x.length; i++) s += x[i]! * x[i + k]!;
    return s;
  };
  let best = 0;
  let lag = 0;
  for (let k = Math.floor(sr / 1200); k < sr / 60; k++) {
    const s = ac(k);
    if (s > best) {
      best = s;
      lag = k;
    }
  }
  const [a, b, c] = [ac(lag - 1), ac(lag), ac(lag + 1)];
  return sr / (lag + (0.5 * (a - c)) / (a - 2 * b + c));
}

const cents = (a: number, b: number): number => Math.abs(1200 * Math.log2(a / b));

/** What's wrong with a set of presets (empty: sound). Without rendering: unique ids, the recipe language's own check
 *  (`checkRecipe`: a layer with no level is silent), a pitched instrument's fundamental at its `ref`, root spacing
 *  within the bounds per tier, a sustain loop inside the render. With `render`: each renders finite and audible, and a
 *  harmonic pitched instrument (not the bell, whose partials are inharmonic) sounds within `cents` of its `ref` (an
 *  octave below is accepted: a sub can win the autocorrelation). */
export function instrumentIssues(rows: readonly InstrumentPreset[], o: { render?: boolean } = {}): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const p of rows) {
    if (seen.has(p.id)) out.push(`${p.id}: duplicate id`);
    seen.add(p.id);
    for (const m of checkRecipe(p.recipe)) out.push(`${p.id}: ${m}`);
    if (p.pitched) {
      const f0 = (p.recipe.layers.find((l) => typeof (l as { f0?: unknown }).f0 === "number") as { f0: number } | undefined)?.f0;
      if (f0 === undefined) out.push(`${p.id}: pitched, but no layer has a fundamental (f0)`);
      else if (cents(f0, midiHz(p.ref)) > 1) out.push(`${p.id}: its fundamental ${f0} Hz is ${cents(f0, midiHz(p.ref)).toFixed(0)} cents from its ref (MIDI ${p.ref}, ${midiHz(p.ref).toFixed(2)} Hz)`);
      const roots = p.roots ?? [p.ref];
      if (widestShift(roots) > INSTRUMENT_BOUNDS.desktopShift) out.push(`${p.id}: desktop roots ${roots.join(", ")} leave a shift of ${widestShift(roots)} semitones (at most ${INSTRUMENT_BOUNDS.desktopShift})`);
      const low = p.lowRoots ?? roots;
      if (widestShift(low) > INSTRUMENT_BOUNDS.phoneShift) out.push(`${p.id}: phone roots ${low.join(", ")} leave a shift of ${widestShift(low)} semitones (at most ${INSTRUMENT_BOUNDS.phoneShift})`);
      if (low.length > roots.length) out.push(`${p.id}: more phone roots (${low.length}) than desktop roots (${roots.length})`);
    } else if (p.roots || p.lowRoots) out.push(`${p.id}: percussion plays at fixed notes; it needs no roots`);
    if (p.sustain) {
      const [s, e] = p.sustain as [number, number];
      if (!(s >= 0 && e - s >= 0.5 && e <= p.recipe.length)) out.push(`${p.id}: sustain loop ${s}–${e} s must be at least 0.5 s long and inside the ${p.recipe.length} s render`);
    }
    if (!o.render) continue;
    const r = renderSound(`inst:${p.id}`, p.recipe, 0, 0, !!p.sustain);
    const x = r.channels[0]!;
    if (!x.every(Number.isFinite)) {
      out.push(`${p.id}: renders non-finite samples`);
      continue;
    }
    const peak = peakOf(r.channels);
    if (!(peak > 0.01)) out.push(`${p.id}: renders silent (peak ${peak.toFixed(4)})`);
    else if (p.pitched && p.family !== "bell") {
      const want = midiHz(p.ref);
      const got = pitchOf(x.subarray(Math.round(0.4 * r.sr), Math.round(0.6 * r.sr)), r.sr);
      const err = Math.min(cents(got, want), cents(got, want / 2));
      if (!(err <= INSTRUMENT_BOUNDS.cents)) out.push(`${p.id}: sounds at ${got.toFixed(1)} Hz for ${want.toFixed(1)} (${err.toFixed(0)} cents off)`);
    }
  }
  return out;
}
